<template lang="pug">
	.web-service-structure-criteria.flex-col.space-large
		.space(:class="[hideCriteria ? 'flex-col' : 'flex-row']")
			dx-validation-group.flex-col.space-large(ref="validationGroup")
				.flex-col.space-large(v-if="(editableParameters.length || service.JsonDefinition.ComplexParameters) && !hideParams")
					.font-big.upper.grey(v-if="!hideHeaders") {{$tCaption('Parameters')}}
					.flex-col.space-medium.full-width.wrap(:class="{ 'padding-left': !hideHeaders }")
						.selectable-param.flex-col.full-width(v-for="param in combinedParams")
							BaseParameterSelector(:parameter="param", :paramValues="paramValues", :paramCaptions="paramCaptions", :defaultValue="serviceParams[param.Name]", :checkValidation="checkValidation",
								:propagatedEditableParams="propagatedEditableParams", @change="$emitChange()", @clear="clearParam(param)", :fullStructure="fullStructure"
							)
								UsedParameterSelector(:param="param", :items="getUsedServicesByParamType(param)", @select="data => onUsedParamSelect(param, data)", :associations="associations", 
									:allowTemplateFieldCreation="allowTemplateFieldCreation", @action="onUsedParamAction")
								slot(name="param-options", :param="param", :data="{ validate: validate }")
				slot(name="parameters", :data="{ validate: validate }")
			.flex-col(v-if="service.JsonDefinition.Outputs.length && !hideCriteria")
				.font-big.upper.grey {{$tCaption('Criteria')}}
				.flex-row.space.full-width(:class="{ 'padding-left': !hideCriteria }")
					FilterBuilder(:fields="columns", v-model="innerCriteria")
</template>

<script>
	import _ from 'lodash';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxDateBox from 'devextreme-vue/date-box';
	import FilterBuilder from 'app-core/FilterBuilder.vue';
	import { DxNumberBox } from 'devextreme-vue/number-box';
	import DxValidationGroup from 'devextreme-vue/validation-group';
	import DxValidator, { DxRequiredRule, DxCustomRule } from 'devextreme-vue/validator';
	import DxCheckBox from 'devextreme-vue/check-box';
	import ParentServiceParam from './ParentServiceParam.vue';
	import ComplexParameterSelector from './ComplexParameterSelector.vue';
	import UsedParameterSelector from './UsedParameterSelector.vue';
	import serviceHelper from './service-helper';
	import BaseParameterSelector from './BaseParameterSelector.vue';

	export default {
		translationCategory: 'webServiceCaller.StructureCriteria',
		name: 'web-service-structure-criteria',
		components: {
			DxSelectBox, DxTextBox, FilterBuilder, DxDateBox, DxNumberBox, DxValidationGroup, DxValidator, DxRequiredRule, DxCustomRule, DxCheckBox,
			ParentServiceParam, ComplexParameterSelector, UsedParameterSelector, BaseParameterSelector
		},
		model: {
			prop: 'criteria',
			event: 'change'
		},
		props: ['service', 'criteria', 'params', 'paramsCaptions', 'hideCriteria', 'hideParams', 'onlyEditableParams', 
			'hideHeaders', 'outputParamName', 'propagatedEditableParamsList', 'usedServices', 'associations', 'paramsResolver',
			'allowTemplateFieldCreation', 'fullStructure', 'preventStoreParameters'],
		data() {
			if (__DEBUG__)
				window.serviceCriteria = this;

			return {
				innerCriteria: this.criteria,
				dateDropDownOptions: {
					toolbarItems: [
						{
							widget: 'dxButton',
							toolbar: 'bottom',
							location: 'center',
							options: {
								text: () => this.$tCaption('Today'),
								onClick: () => {
									this.$refs.datebox[0].instance.option('value', new Date());
									this.$refs.datebox[0].instance.close();
								}
							}
						}
					]
				},
				checkValidation: false,
				requiredCustomIds: {},
				propagatedEditableParams: this.propagatedEditableParamsList || {},
				cachedUsedServicesByParams: {},
				paramValues: {},
				paramCaptions: {},
				serviceParams: {},
				lastUsedParameters: {}
			};
		},
		computed: {
			editableParameters() {
				const params = (this.service.JsonDefinition.Parameters || []).filter(f => f.UserEditable && (!this.outputParamName || !f.TargetOutputs?.length || f.TargetOutputs.includes(this.outputParamName)));
				return this.filterParams(params, false);
			},
			columns() {
				return this.service.JsonDefinition.Outputs.filter(f => !f.Status).map(f => {
					const dataType = f.DataType === 'list' || f.DataType === 'range' ? 'string' : f.DataType;
					return {
						caption: f.Caption || f.Name,
						dataField: f.Name,
						dataType,
						lookup: f.DataType === 'list' || f.DataType === 'range' ? {
							dataSource: f.ListOptions.map(f => ({ key: f.Key, value: f.Value || f.Key })),
							allowClearing: true,
							displayExpr: 'value',
							valueExpr: 'key'
						} : null
					};
				});
			},
			otherUsedServices() {
				return (this.usedServices || []).filter(f => f.service?.Oid !== this.service?.Oid);
			},
			combinedParams() {
				return [
					...this.filterParams(this.service.JsonDefinition.ComplexParameters, true),
					...this.editableParameters,
				]
			}
		},
		watch: {
			innerCriteria: {
				handler() {
					this.$emitChange();
				},
				deep: true
			},
			paramValues: {
				handler() {
					this.$emitChange();
				},
				deep: true
			},
			usedServices() {
				this.cachedUsedServicesByParams = {};
			},
			service: {
				handler() {
					this.init()
				},
				deep: true
			}
		},
		mounted() {
			if (__DEBUG__) {
				window.structureCriteria = this;
				window.serviceHelper1 = serviceHelper;
			}

			this.init();
		},
		destroyed() {
			delete this.$emitChange;
			delete this.$customValidationAdapter;
			delete this.$validationCallbacks;
		},
		methods: {
			isComplexParam(param) {
				return !!param?.Keys;
			},
			async getEditableParamsNames() {
				const editableParameters = await serviceHelper.getEditableParameters(this.service, void 0, void 0, void 0, true) || [];
				(this.service?.JsonDefinition?.ComplexParameters || []).map(f => {
					if (f?.Keys) {
						editableParameters.push(...f?.Keys.map(key => `${f.Name}_${key}`));
					}
					editableParameters.push(f.Name);
				});
				return editableParameters;
			},
			async init() {
				const editableParameters = await this.getEditableParamsNames();

				const lastUsedParameters = serviceHelper.getLastUsedParamsForService(this.service, this.params, this.paramsCaptions);
				const params = _.clone(this.params && Object.keys(this.params).length ? this.params : _.pick(lastUsedParameters?.value?.paramValues, editableParameters));
				const paramsCaptions = _.clone(this.params && Object.keys(this.params).length ? this.paramsCaptions : _.pick(lastUsedParameters?.value?.paramCaptions, editableParameters));
				const serviceParams = {};
				(this.service.JsonDefinition.Parameters || []).filter(f => editableParameters.includes(f.Name)).map(f => {
					serviceParams[f.Name] = this.valueResolver(f, f.Value);
					if (params[f.Name] === void 0) {
						params[f.Name] = serviceParams[f.Name];
						if (f.ValueCaption) {
							paramsCaptions[f.Name] = f.ValueCaption;
						}
					}
				});

				this.paramValues = params || {};
				this.paramCaptions = paramsCaptions || {},
				this.serviceParams = serviceParams || {},
				this.lastUsedParameters = lastUsedParameters || {};

				this.$emitChange = _.debounce(this.emitChange, 1, {
					'leading': false,
					'trailing': true
				});
				this.$emit('mounted');
				if (Object.keys(this.lastUsedParameters.value.paramValues)) {
					this.$emit('change', this.innerCriteria, this.paramValues, this.paramCaptions);
				}
				(this.service?.JsonDefinition?.Parameters || []).filter(f => f.UserEditable)?.map(f => this.propagatedEditableParams[f.Name] = true);
				(this.service?.JsonDefinition?.ComplexParameters || []).map(f => {
					if (f?.Keys) {
						f?.Keys.map(key => this.propagatedEditableParams[`${f.Name}_${key}`] = true);
					}
				});
				this.$emitChange();
			},
			onUsedParamAction(action, param, item) {
				if (__DEBUG__)
					console.debug('onUsedParamAction', { action, param, item });
				this.$emit('action', action, param, item);
			},
			filterParams(params, isComplex) {
				return (this.paramsResolver ? this.paramsResolver(params, isComplex) : params) || [];
			},
			saveParams() {
				if (this.lastUsedParameters && !this.preventStoreParameters) {
					this.lastUsedParameters.value.paramValues = _.mergeWith(this.lastUsedParameters.value.paramValues || {}, _.cloneDeep(this.paramValues), (a,b) => typeof(b) === 'string' && b.indexOf('[') > -1 ? a : b);
					this.lastUsedParameters.value.paramCaptions = _.mergeWith(this.lastUsedParameters.value.paramCaptions || {}, _.cloneDeep(this.paramCaptions), (a,b) => typeof(b) === 'string' && b.indexOf('[') > -1 ? a : b);
				}
			},
			clearParam(param) {
				const isComplex = this.isComplexParam(param);
				if (this.hasTemplateFieldValues(param)) {
					this.$emit('template-field-change', param, null, isComplex);
				}
				if (isComplex) {
					this.setComplexParam(param, {});
				} else {
					this.setParam(param.Name, param.Value);
				}
			},
			setComplexParam(param, values) {
				param.Keys.map(f => {
					this.setParam(`${param.Name}_${f}`, values[f]);
				});
			},
			setParam(paramName, value) {
				this.$set(this.paramValues, paramName, value);
				this.$emitChange();
			},
			hasTemplateFieldValues(param) {
				return (this.associations || {})[param.Name];
			},
			async onUsedParamSelect(param, usedParam) {
				const isComplex = this.isComplexParam(param);
				if (__DEBUG__)
					console.debug('On used param select', { param, usedParam, isComplex });

				if (usedParam?.param?.templateField) {
					this.$emit('template-field-change', param, usedParam?.param?.templateField?.id, isComplex);
					return;
				}

				if (usedParam?.__type === 'customField') {
					if (isComplex) {
						param.Keys.map(f => {
							this.setParam(`${param.Name}_${f}`, usedParam?.complexValue && usedParam?.complexValue[f]);
						});
					} else {
						this.$set(this.paramValues, param.Name, usedParam?.insertValue);
					}
					return;
				}

				if (!usedParam) {
					this.clearParam(param)
				} else {
					if (isComplex) {
						param.Keys.map(f => {
							this.setParam(`${param.Name}_${f}`, `[${usedParam.param.Name}_${f}@params.${usedParam.service.Code}]`);
						});
						return;
					}
					if (usedParam.__type === 'output')
						this.$set(this.paramValues, param.Name, `[${usedParam.param.Name}@outputs.${usedParam.service.Code}]`);
					if (usedParam.__type === 'param')
						this.$set(this.paramValues, param.Name, `[${usedParam.param.Name}@params.${usedParam.service.Code}]`);

					if (usedParam?.param?.ParentServiceOid) {
						const editableParamsNames = await serviceHelper.getParentEditableParameters(usedParam.param, [usedParam.param.Name]);
						editableParamsNames.map(paramName => {
							if (this.service.JsonDefinition.Parameters.some(p => p.Name === paramName)) {
								this.$set(this.paramValues, paramName, `[${paramName}@params.${usedParam.service.Code}]`);
							}
						});
					}
				}
			},
			getUsedServicesByParamType(param) {
				const isComplex = this.isComplexParam(param);
				if (!this.usedServices) return [];
				const type = isComplex ? param.Type : param.DataType;
				const parentServiceOid = param.ParentServiceOid;
				const key = `${isComplex ? 'complex' : 'param'}_${type}_${param?.Name}`;

				if (this.cachedUsedServicesByParams[key])
					return this.cachedUsedServicesByParams[key];

				const filteredServices = (this.usedServices || []).map(f => f.service).filter(f => f && f.Oid !== this.service?.Oid).map(service => ({
					service,
					params: isComplex ? 
						service?.JsonDefinition?.ComplexParameters?.filter(cp => cp.Type === type) 
							.map(f => ({ __type: 'complex', ...f }))
						: 
						[	...(service?.JsonDefinition?.Parameters?.filter(p => p.UserEditable && (!type || p.DataType == type) && (!parentServiceOid || p.ParentServiceOid === parentServiceOid)))
								.map(f => ({ ...f, __type: 'param' })),
							...(service?.JsonDefinition.Outputs?.filter(o => o.DataType === type))
								.map(f => ({ ...f, __type: 'output' }))
						]
				}));
				
				const templateFields = (this.usedServices || []).filter(f => f.__type === 'templateFields').map(templateFieldHolder => ({
					service: {
						Name: templateFieldHolder.text
					},
					params: [
						...(templateFieldHolder.items.filter(f => serviceHelper.isSupportedTemplateFieldForParam(f.templateField, param, isComplex)).map(f => ({
							...f,
							Name: f.text,
							icon: 'f'
						}))),
						...(this.allowTemplateFieldCreation ? [{
							Name: this.$tCaption('Generate new template field'),
							template: 'output',
							action: 'create-template-field',
							icon: 'plus',
							preventClose: true
						}] : [])
					]
				}));

				const allowedCustomField = (f) => f.allowFieldValue && ((isComplex && f.complexType === type) || (!isComplex && f.insertValue));
				const customFields = (this.usedServices || []).filter(f => f.__type === 'customFields' && (f.items || []).some(f => allowedCustomField(f))).map(customFieldHolder => ({
					service: {
						Name: customFieldHolder.text
					},
					params: [
						...(customFieldHolder.items.filter(f => allowedCustomField(f)).map(f => ({
							icon: 'f',
							__type: 'customField',
							...f,
							Name: f.text
						})))
					]
				}));

				if (__DEBUG__)
					console.debug('getUsedServicesByParamType', { filteredServices, param, isComplex, templateFields, customFields });
				
				const result = [...customFields, ...templateFields, ...filteredServices].filter(f => f.params?.length);
				this.$set(this.cachedUsedServicesByParams, key, result);

				return result;
			},
			async validate() {
				this.checkValidation = true;
				return new Promise(res => {
					this.$nextTick(() => {
						if (this.$validationCallbacks)
							this.$validationCallbacks.map(f => f());
						const isValid = this.$refs.validationGroup.instance.validate().isValid;
						res(isValid);
						return isValid;
					});
				});
			},
			hasComplexParamValue(param) {
				const hasValue = (param?.Keys || []).some(key => this.paramValues[`${param.Name}_${key}`]);
				if (__DEBUG__)
					console.debug('hasComplexParamValue', { param, v: this.paramValues, hasValue });
				return hasValue;
			},
			valueResolver(param, value) {
				return this.$Data.externalServiceGetParamValue(param, value);
			},
			async emitChange(preventSave) {
				const editableParams = await this.getEditableParamsNames();

				if (__DEBUG__)
					console.debug('Emit change structure criteria', { criteria: this.innerCriteria, params: this.paramValues, caps: this.paramCaptions, editableParams });
				
				this.$emit('change', this.innerCriteria, _.pick(this.paramValues, editableParams), _.pick(this.paramCaptions, editableParams), this.propagatedEditableParams);
				if (!preventSave)
					this.saveParams();
			}
		}
	};
</script>
<style lang='scss'>
	.web-service-structure-criteria {
		@import "main/themes/generated/variables.base.scss";

		.dx-filterbuilder {
			background-color: transparent !important;
		}

		.selectable-param {
			min-width: 340px;
			margin-bottom: 14px;
		}

		.padding-left {
			padding-left: 20px;
		}

		.is-required {
			color: #d90000;
			font-size: 22px;
		}

		.used-param {
			max-width: 270px;
		}
	}
</style>
